import * as React from 'react';

import {
  enumPaymentMethod,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import {
  $columnSelectedMethods,
  $offset,
} from '~/modules/finance/_state/financePayments.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface MethodOption {
  id: PaymentMethod;
  label: string;
}

const visibleMethods: PaymentMethod[] = [
  enumPaymentMethod.Check,
  enumPaymentMethod.ACH,
  enumPaymentMethod.Wire,
  enumPaymentMethod.Cash,
  enumPaymentMethod.ATM,
];

interface PaymentMethodColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ PAYMENT METHOD COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const PaymentMethodColumnFilter: React.FC<
  PaymentMethodColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedMethodsSet, setSelectedMethodsSet] = useAtom(
    $columnSelectedMethods,
  );
  const setOffset = useSetAtom($offset);

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const selectedMethods = React.useMemo(
    () => Array.from(selectedMethodsSet),
    [selectedMethodsSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedMethods),
    [selectedMethods],
  );

  const methodOptions: MethodOption[] = React.useMemo(
    () =>
      visibleMethods.map((method) => ({
        id: method,
        label: t.payments.list.methodFilter.options[method] ?? method,
      })),
    [t.payments.list.methodFilter.options],
  );

  const filteredMethodOptions = React.useMemo(
    () =>
      methodOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [methodOptions, searchQuery],
  );

  const selectedMethodOptions = React.useMemo(
    () => methodOptions.filter((option) => selectedMethods.includes(option.id)),
    [methodOptions, selectedMethods],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedMethodsSet(new Set(Array.from(keys) as PaymentMethod[]));
      setOffset(0);
    },
    [setSelectedMethodsSet, setOffset],
  );

  const handleReset = React.useCallback(() => {
    setSelectedMethodsSet(new Set());
    setOffset(0);
  }, [setSelectedMethodsSet, setOffset]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: MethodOption, isSelected: boolean) => (
      <FilterLabel label={item.label} isSelected={isSelected} />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@PaymentMethodColumnFilter min-w-[250px]"
      entries={filteredMethodOptions}
      selectedEntries={selectedMethodOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(method) => method.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.payments.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
